import React, { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon } from '../icons/MicrophoneIcon';
import { StopCircleIcon } from '../icons/StopCircleIcon';

export const AudioRecorder = ({ onRecordingComplete }) => {
    const [recordingStatus, setRecordingStatus] = useState('idle');
    const [audioUrl, setAudioUrl] = useState(null);
    const [error, setError] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const [timer, setTimer] = useState(0);
    const timerIntervalRef = useRef(null);

    const startTimer = () => {
        setTimer(0);
        timerIntervalRef.current = window.setInterval(() => {
            setTimer(prev => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
    };

    const startRecording = async () => {
        setError(null);
        setRecordingStatus('recording');
        audioChunksRef.current = [];
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = event => {
                audioChunksRef.current.push(event.data);
            };
            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const url = URL.createObjectURL(audioBlob);
                setAudioUrl(url);
                setRecordingStatus('recorded');
                
                const reader = new FileReader();
                reader.onloadend = () => {
                    onRecordingComplete({
                        name: `recording-${new Date().toISOString()}.webm`,
                        data: reader.result,
                        mimeType: 'audio/webm',
                    });
                };
                reader.readAsDataURL(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorderRef.current.start();
            startTimer();
        } catch (err) {
            console.error("Error accessing microphone:", err);
            setError("Could not access microphone. Please check permissions.");
            setRecordingStatus('error');
            stopTimer();
        }
    };
    
    const stopRecording = () => {
        if (mediaRecorderRef.current && recordingStatus === 'recording') {
            mediaRecorderRef.current.stop();
            stopTimer();
        }
    };

    const resetRecording = () => {
        setRecordingStatus('idle');
        setAudioUrl(null);
        setError(null);
        setTimer(0);
    };
    
    useEffect(() => {
        return () => {
            stopTimer();
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            mediaRecorderRef.current?.stream?.getTracks().forEach(track => track.stop());
        };
    }, []);

    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    return (
        <div className="flex flex-col items-center justify-center p-8 space-y-6 h-60">
            {error && <div className="text-red-400">{error}</div>}
            
            {recordingStatus === 'idle' && (
                <button type="button" onClick={startRecording} className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-full flex items-center transition-colors">
                    <MicrophoneIcon className="w-6 h-6 mr-2" />
                    Start Recording
                </button>
            )}

            {recordingStatus === 'recording' && (
                <>
                    <div className="text-2xl font-mono text-purple-300">{formatTime(timer)}</div>
                    <button type="button" onClick={stopRecording} className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-3 px-6 rounded-full flex items-center transition-colors">
                        <StopCircleIcon className="w-6 h-6 mr-2" />
                        Stop Recording
                    </button>
                </>
            )}
            
            {recordingStatus === 'recorded' && audioUrl && (
                <>
                    <audio src={audioUrl} controls className="w-full" />
                    <button type="button" onClick={resetRecording} className="text-sm text-gray-400 hover:text-white">Record Again</button>
                </>
            )}
        </div>
    );
};
